import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from './modal'
import { Spinner } from './spinner'
import { FlowProgress } from './flow-progress'
import { PlatformMark } from './platform-chip'
import { Ticker } from './ticker'
import { TokenIcon } from './holdings-table'
import { useToast } from './toast'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { useRefreshActivity } from '@/lib/activity-feed'
import { krakenFeeItems } from '@/lib/activity'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { formatPct, formatTokenAmount, formatUsd } from '@/lib/format'
import type { RouterOutputs } from '@/lib/api-types'

export type KrakenSellAsset = 'SOL' | 'USDC' | 'USDT'
/** the mint behind each asset Kraken sells, for its logo */
export const KRAKEN_ASSET_MINTS: Record<KrakenSellAsset, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT }

type SellResult = RouterOutputs['kraken']['sell']

const STEPS = ['Placing', 'Filling', 'Done'] as const

/** a number typed in; blanks, zeros and nonsense are nothing */
function parse(value: string): number | undefined {
  const n = Number(value)
  return value.trim() !== '' && Number.isFinite(n) && n > 0 ? n : undefined
}

/** an amount to so many places, rounded down, without trailing zeros */
function floorTo(value: number, places: number) {
  const scale = 10 ** places
  return (Math.floor(value * scale + 1e-6) / scale).toFixed(places).replace(/\.?0+$/, '')
}

/** the best bid as it stands, with a live dot: the figure refreshes every few seconds while the dialog is open */
function BestBid({ price }: { price: number }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted" title="Live from Kraken's order book">
      <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-accent" aria-hidden="true" />
      Best bid {formatUsd(price)}
    </span>
  )
}

/**
 * Sells SOL, USDC or USDT held on Kraken for USD (John, Nov 1 2026), the way
 * he does it by hand on Kraken Pro: a limit a couple of cents under the best
 * bid, so the order fills at once at the maker/taker fee instead of Convert's
 * 1% plus spread. The minimum price follows the order book until one is
 * typed in; whatever the bids at that price do not take is cancelled, never
 * left on the book.
 */
export function KrakenSellModal({ asset, onClose }: { asset: KrakenSellAsset | null; onClose: () => void }) {
  const trpc = useTRPC()
  const toast = useToast()
  const refresh = useRefreshActivity()
  const [amount, setAmount] = useState('')
  const [price, setPrice] = useState('')
  const [priceEdited, setPriceEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SellResult | null>(null)
  useEffect(() => {
    if (asset) {
      setAmount('')
      setPrice('')
      setPriceEdited(false)
      setError(null)
      setResult(null)
    }
  }, [asset])
  const volume = parse(amount)
  const customPrice = priceEdited ? parse(price) : undefined
  const quote = useQuery({
    ...trpc.kraken.sellQuote.queryOptions({ asset: asset ?? 'SOL', volume, price: customPrice }),
    enabled: asset !== null && result === null,
    // the bids move every few seconds (John, Nov 2 2026: "would be nice" if it kept up); the book is public, the balance behind it is cached
    refetchInterval: 3_000,
    placeholderData: keepPreviousData,
    retry: false,
  })
  const q = quote.data
  /** the figures for the amount typed in; nothing until there is one */
  const live = volume !== undefined ? q : undefined
  const places = q?.pairDecimals ?? 2
  // the minimum price follows the book until one is typed in
  const shownPrice = priceEdited ? price : q ? q.price.toFixed(places) : ''
  const limit = priceEdited ? customPrice : q?.price
  const available = q?.available ?? 0
  const symbol: KrakenSellAsset = asset ?? 'SOL'
  const logo = tokenLogo(KRAKEN_ASSET_MINTS[symbol])
  const fmt = (value: number) => formatTokenAmount(value, symbol)
  const sell = useMutation(
    trpc.kraken.sell.mutationOptions({
      onSuccess: (res) => {
        setResult(res)
        refresh(['kraken'])
        if (res.outcome === 'filled' || res.outcome === 'partial') {
          toast('success', <>Sold {formatTokenAmount(res.volumeExecuted, res.asset)} <Ticker symbol={res.asset} imageUrl={tokenLogo(KRAKEN_ASSET_MINTS[res.asset])} /></>, `${formatUsd(res.netUsd)} on Kraken`)
        } else if (res.outcome === 'unfilled') {
          toast('info', 'Nothing sold', 'No bids at that price. Try again.')
        }
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  const tooMuch = volume !== undefined && volume > available + 1e-9
  const tooSmall = live !== undefined && volume !== undefined && volume < live.orderMin
  const belowCostMin = live !== undefined && live.costMin !== null && live.fillVolume > 0 && live.grossUsd < live.costMin
  const canSell = asset !== null && volume !== undefined && limit !== undefined && q !== undefined && !tooMuch && !tooSmall && !belowCostMin && !sell.isPending && result === null
  function submit() {
    if (!asset || volume === undefined) return setError('Enter an amount.')
    if (limit === undefined) return setError('Enter a minimum price.')
    setError(null)
    sell.mutate({ asset, volume, price: limit })
  }
  const pct = (n: number) => {
    if (!q) return
    setAmount(n === 100 ? floorTo(q.available, q.lotDecimals) : floorTo((q.available * n) / 100, Math.min(q.lotDecimals, 6)))
  }
  /** after a partial or empty fill: the same dialog again for what is left */
  function again(res: SellResult) {
    const left = Math.max(0, res.volume - res.volumeExecuted)
    setAmount(left > 0 ? floorTo(left, q?.lotDecimals ?? 8) : '')
    setPriceEdited(false)
    setPrice('')
    setResult(null)
    setError(null)
  }
  const outcomeText = (res: SellResult) => {
    switch (res.outcome) {
      case 'filled':
        return `Done. ${formatUsd(res.netUsd)} arrived.`
      case 'partial':
        return `Sold ${fmt(res.volumeExecuted)} of ${fmt(res.volume)} ${res.asset} for ${formatUsd(res.netUsd)}. The rest did not sell at this price.`
      case 'unfilled':
        return 'Nothing sold at this price.'
      case 'checked':
        return 'Checked with Kraken. Nothing was sold on this run.'
      case 'failed':
        return res.error ?? 'The sale did not go through.'
      default:
        return 'Selling…'
    }
  }

  return (
    <Modal
      open={asset !== null}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <PlatformMark id="kraken" size="size-5" />
          Sell {symbol}
        </span>
      }
      footer={
        result ? (
          <button type="button" className="btn btn-secondary w-full" onClick={onClose}>Done</button>
        ) : (
          <button type="button" className="btn btn-primary w-full" disabled={!canSell} onClick={submit}>
            {/* no loader in the button (John, Nov 2 2026: "flashy"): the progress card above carries the bar */}
            {sell.isPending ? 'Selling…' : volume !== undefined ? `Sell ${fmt(volume)} ${symbol}` : 'Sell'}
          </button>
        )
      }
    >
      {asset ? (
        <div className="grid gap-3">
          {sell.isPending || result ? (
            <FlowProgress
              labels={STEPS}
              step={result ? 2 : 1}
              done={result !== null && result.outcome !== 'failed'}
              failed={result?.outcome === 'failed'}
              status={result ? outcomeText(result) : `Selling ${fmt(volume ?? 0)} ${symbol}…`}
              fees={result ? krakenFeeItems(result.feeUsd) : undefined}
              action={
                result && (result.outcome === 'partial' || result.outcome === 'unfilled') ? (
                  <button type="button" className="btn btn-secondary" onClick={() => again(result)}>
                    {result.outcome === 'partial' ? 'Sell the rest' : 'Try again'}
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="card grid gap-3 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Amount</span>
                  <span className="flex gap-2 text-xs text-accent">
                    {[25, 50, 100].map((n) => (
                      <button key={n} type="button" onClick={() => pct(n)} disabled={!q}>
                        {n === 100 ? 'Max' : `${n}%`}
                      </button>
                    ))}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    className="w-0 min-w-0 flex-1 bg-transparent text-3xl font-semibold tabular-nums outline-none placeholder:text-muted"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {/* the same size as the swap dialog's token pill (John, Nov 2 2026: "the sol logo was small") */}
                  <span className="pill flex items-center gap-2 bg-surface text-sm">
                    <TokenIcon symbol={symbol} imageUrl={logo} size="size-6" />
                    {symbol}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted">
                  <span>{live ? formatUsd((volume ?? 0) * live.bestBid) : '$0.00'}</span>
                  {q ? <span>On Kraken {fmt(q.available)} {symbol}</span> : <span className="skeleton inline-block h-3 w-24 align-middle" />}
                </div>
              </div>
              <div className="card grid gap-2 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Minimum price</span>
                  {priceEdited ? (
                    <button
                      type="button"
                      className="text-xs text-accent"
                      onClick={() => {
                        setPriceEdited(false)
                        setPrice('')
                      }}
                    >
                      Auto
                    </button>
                  ) : q ? (
                    <BestBid price={q.bestBid} />
                  ) : (
                    <span className="text-xs text-muted" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-semibold text-muted">$</span>
                  <input
                    className="w-0 min-w-0 flex-1 bg-transparent text-xl font-semibold tabular-nums outline-none placeholder:text-muted"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={shownPrice}
                    onChange={(e) => {
                      setPrice(e.target.value)
                      setPriceEdited(true)
                    }}
                  />
                </div>
                {priceEdited && q ? <BestBid price={q.bestBid} /> : null}
                {live && !live.fillable ? (
                  <p className="text-xs text-warning">Only {fmt(live.fillVolume)} {symbol} sells at this price right now. The rest would not.</p>
                ) : null}
              </div>
              <div className="card grid gap-1 p-4 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted">You receive (est.)</span>
                  <span className="font-medium tabular-nums">
                    {quote.isFetching && !q ? <Spinner className="size-4" /> : live && live.fillVolume > 0 ? `≈ ${formatUsd(live.netUsd)}` : '–'}
                  </span>
                </div>
                {live && live.feePct !== null && live.feeUsd !== null && live.fillVolume > 0 ? (
                  <div className="flex items-center justify-between gap-3 text-xs text-muted">
                    <span>Fee {formatPct(live.feePct, false)}</span>
                    <span className="tabular-nums">{formatUsd(live.feeUsd)}</span>
                  </div>
                ) : null}
              </div>
              {q?.dryRun ? <p className="text-xs text-muted">Test run: Kraken checks the order, nothing is sold.</p> : null}
              {tooMuch ? <p className="text-xs text-negative">More than the {fmt(available)} {symbol} on Kraken.</p> : null}
              {tooSmall && live ? <p className="text-xs text-negative">Kraken sells at least {fmt(live.orderMin)} {symbol}.</p> : null}
              {belowCostMin && live && live.costMin !== null ? <p className="text-xs text-negative">Below Kraken's {formatUsd(live.costMin)} minimum.</p> : null}
              {quote.isError ? <p className="text-xs text-negative">{errorMessage(quote.error)}</p> : null}
              {error ? <p className="text-sm text-negative">{error}</p> : null}
            </>
          )}
        </div>
      ) : null}
    </Modal>
  )
}
